// Portiert aus frontend/shared/field-type-catalog.js.
// Zentraler Katalog aller im Wartungs-Formular-Builder unterstützten Feldtypen
// inkl. Aliasen, damit Legacy-Schemas (z. B. "bool", "textarea") korrekt
// auf die kanonischen Typen normalisiert werden.

export interface FieldTypeEntry {
  type: string;
  label: string;
  aliases: string[];
  runtimeRenderer: string;
  builderSupported: boolean;
  adminTemplateSupported: boolean;
}

export const FIELD_TYPE_CATALOG: FieldTypeEntry[] = [
  { type: "text", label: "Text", aliases: ["text", "string", "textarea", "email"], runtimeRenderer: "text", builderSupported: true, adminTemplateSupported: true },
  { type: "number", label: "Zahl", aliases: ["number", "numeric", "decimal", "int", "integer"], runtimeRenderer: "number", builderSupported: true, adminTemplateSupported: true },
  { type: "date", label: "Datum", aliases: ["date"], runtimeRenderer: "date", builderSupported: true, adminTemplateSupported: true },
  { type: "datetime", label: "Datum + Uhrzeit", aliases: ["datetime", "datetime-local"], runtimeRenderer: "datetime", builderSupported: true, adminTemplateSupported: true },
  { type: "checkbox", label: "Checkbox (Ja/Nein)", aliases: ["checkbox", "bool", "boolean"], runtimeRenderer: "checkbox", builderSupported: true, adminTemplateSupported: true },
  { type: "select", label: "Auswahl", aliases: ["select", "dropdown", "choice"], runtimeRenderer: "select", builderSupported: true, adminTemplateSupported: true },
  { type: "multiselect", label: "Mehrfachauswahl", aliases: ["multiselect", "multi-select", "multi_select", "choice_multi", "select_multiple"], runtimeRenderer: "multiselect", builderSupported: true, adminTemplateSupported: true },
  { type: "file", label: "Datei-Upload", aliases: ["file", "upload", "attachment"], runtimeRenderer: "file", builderSupported: true, adminTemplateSupported: true },
  { type: "photo", label: "Foto-Upload", aliases: ["photo", "image", "image-upload", "camera"], runtimeRenderer: "photo", builderSupported: true, adminTemplateSupported: true },
  { type: "pdf-upload", label: "PDF-Upload", aliases: ["pdf-upload", "pdf", "pdf_upload"], runtimeRenderer: "pdf-upload", builderSupported: true, adminTemplateSupported: true },
  { type: "geo", label: "Geokoordinate", aliases: ["geo", "geolocation", "location", "gps"], runtimeRenderer: "geo", builderSupported: true, adminTemplateSupported: true },
  { type: "formula", label: "Formel (readonly)", aliases: ["formula", "calculated", "computed"], runtimeRenderer: "formula", builderSupported: true, adminTemplateSupported: true },
  { type: "signature", label: "Signatur", aliases: ["signature", "sign", "digital_signature"], runtimeRenderer: "signature", builderSupported: true, adminTemplateSupported: true },
  { type: "article_ref", label: "Artikelreferenz", aliases: ["article_ref", "article-reference", "artikel_ref", "article"], runtimeRenderer: "article_ref", builderSupported: true, adminTemplateSupported: true },
  { type: "article_ref_list", label: "Artikelreferenz-Liste", aliases: ["article_ref_list", "article-reference-list", "artikel_ref_liste", "article_list"], runtimeRenderer: "article_ref_list", builderSupported: true, adminTemplateSupported: true },
  { type: "material_link", label: "Materialverknüpfung", aliases: ["material_link", "material-link", "materialverknuepfung", "material_verknuepfung"], runtimeRenderer: "material_link", builderSupported: true, adminTemplateSupported: true },
  { type: "linked_asset", label: "Verknüpftes Wartungsobjekt", aliases: ["linked_asset", "asset_link", "asset-link", "linked-asset", "linked-object", "verknuepftes_objekt"], runtimeRenderer: "linked_asset", builderSupported: true, adminTemplateSupported: true },
  { type: "static_text", label: "Statischer Text", aliases: ["static_text", "info_text", "heading", "notice"], runtimeRenderer: "static_text", builderSupported: true, adminTemplateSupported: true },
  { type: "page_break", label: "Seitenumbruch", aliases: ["page_break", "page-break", "seitenumbruch"], runtimeRenderer: "page_break", builderSupported: true, adminTemplateSupported: true },
  { type: "divider", label: "Trennlinie", aliases: ["divider", "separator", "trennlinie"], runtimeRenderer: "divider", builderSupported: true, adminTemplateSupported: true },
];

const TYPE_ALIAS_MAP = new Map<string, string>();
for (const entry of FIELD_TYPE_CATALOG) {
  for (const alias of [entry.type, ...(entry.aliases || [])]) {
    TYPE_ALIAS_MAP.set(String(alias).trim().toLowerCase(), entry.type);
  }
}

export const FIELD_TYPE_BY_KEY = new Map<string, FieldTypeEntry>(
  FIELD_TYPE_CATALOG.map((entry) => [entry.type, entry]),
);

export function normalizeFieldType(rawType: unknown): string | null {
  const normalized = String(rawType ?? "").trim().toLowerCase();
  return TYPE_ALIAS_MAP.get(normalized) || null;
}

export function getFieldTypeMeta(rawType: unknown): FieldTypeEntry | null {
  const type = normalizeFieldType(rawType);
  if (!type) return null;
  return FIELD_TYPE_BY_KEY.get(type) || null;
}

export function getSupportedFieldTypes(): FieldTypeEntry[] {
  return FIELD_TYPE_CATALOG.filter((entry) => entry.builderSupported);
}

export function getFieldTypeLabel(rawType: unknown): string {
  const meta = getFieldTypeMeta(rawType);
  if (meta) return meta.label;
  const raw = String(rawType ?? "").trim();
  return raw || "—";
}

/** Layout-Felder ohne echten Eingabewert (kein Pflichtfeld möglich). */
export const LAYOUT_FIELD_TYPES = new Set(["page_break", "divider", "static_text"]);

/** Feldtypen mit Options-Liste (Auswahl-Editor). */
export const OPTION_FIELD_TYPES = new Set([
  "select",
  "multiselect",
  "article_ref",
  "article_ref_list",
]);

/** Semantische Rollen für Inspektionsfelder. */
export const SEMANTIC_ROLES = [
  { value: "info", label: "Information" },
  { value: "defect", label: "Mangel" },
  { value: "material", label: "Material" },
  { value: "measurement", label: "Messwert" },
  { value: "grade", label: "Bewertung" },
] as const;

export type SemanticRole = (typeof SEMANTIC_ROLES)[number]["value"];

/** Farben der Bewertungsnoten 1–6 (iOS-Parität). */
export const MAINTENANCE_GRADE_COLORS: Record<number, string> = {
  1: "#16a34a",
  2: "#65a30d",
  3: "#ca8a04",
  4: "#ea580c",
  5: "#dc2626",
  6: "#7f1d1d",
};

export const ALL_GRADES = [1, 2, 3, 4, 5, 6];
